/**
 * **The Odżywianie stage card** — the target line, the range's sentence, and the chart, decided
 * together so the words over the plot and the bars under them describe one range.
 *
 * The chart is a column per DAY (7 and 28 days) or per WEEK (12 weeks, the week's average), never
 * an instant on a time axis: the server's unit is the client's local day, and a day nobody logged
 * is a column with a hollow mark rather than a gap in a line.
 *
 * ── A bar's ink is the server's verdict on ITS nutrient ───────────────────────────────────────
 * Every day and every week carries the server's verdict per nutrient (R9, R10): a week's bar is its
 * average judged against the week's target with a day's margins, and the week holding today is in
 * progress. This module only copies the verdict onto the column; it compares nothing.
 */

import type { Translate } from '~/shared/lib/localeTranslate';
import { dateTimeFormat } from '~/shared/lib/intlCache';
import { capitaliseFirst } from '~/shared/lib/localeCase';
import { parseIsoDate } from '~/shared/lib/calendarDate';

import {
  atLeast,
  dayMonth,
  isLogged,
  isPartial,
  oneDecimal,
  signedWhole,
  targetOf,
  targetRuleOf,
  toldStatus,
  unitOf,
  weekBundles,
  weekSpanLabel,
  wholeNumber,
  withUnit,
  type Nutrient,
  type NutritionDay,
  type NutritionMiss,
  type NutritionRange,
  type NutritionSpan,
  type NutritionTargetValues,
  type NutritionVerdict,
} from './nutrition';

const KEY = 'web:clientDetail.nutrition';

export interface NutritionChartColumn {
  readonly key: string;
  /** The x label, or `null` where the axis skips this column. */
  readonly label: string | null;
  /** Today, or the week holding it. */
  readonly current: boolean;
  readonly kind: NutritionVerdict;
  readonly value: number | null;
  /** Sugar inside a carbohydrate bar. */
  readonly sugar: number | null;
  readonly target: number | null;
  /** The figure printed over the bar, where the span prints them. */
  readonly valueLabel: string | null;
  readonly weightKg: number | null;
  /** What the readout and the hidden table say about this column. */
  readonly readout: { readonly title: string; readonly lines: readonly string[] };
}

/** A run of columns governed by one target value: the dashed step line. */
export interface NutritionTargetStep {
  readonly from: number;
  readonly to: number;
  readonly value: number;
}

export interface NutritionWeightLine {
  readonly points: readonly { readonly index: number; readonly kg: number }[];
  readonly lo: number;
  readonly hi: number;
  readonly ticks: readonly { readonly kg: number; readonly label: string }[];
  readonly lastLabel: string;
}

export interface NutritionChartView {
  readonly columns: readonly NutritionChartColumn[];
  readonly unit: 'kcal' | 'g';
  readonly yMax: number;
  readonly yTicks: readonly { readonly value: number; readonly label: string }[];
  readonly steps: readonly NutritionTargetStep[];
  /** The newest column's target, printed in the chip at the right end of the line. */
  readonly lastTarget: { readonly value: number; readonly label: string } | null;
  readonly weight: NutritionWeightLine | null;
  readonly legend: readonly NutritionLegendItem[];
  /** One sentence naming the plot for a screen reader. */
  readonly name: string;
}

export type NutritionLegendItem =
  | 'onTarget'
  | 'offTarget'
  | 'targetBand'
  | 'targetFloor'
  | 'plain'
  | 'noTarget'
  | 'unjudged'
  | 'current'
  | 'sugar'
  | 'average'
  | 'weight'
  | 'weightWeekly';

export interface NutritionTargetLine {
  readonly since: string;
  readonly energy: string;
  readonly macros: readonly { readonly nutrient: Exclude<Nutrient, 'energy'>; readonly value: string | null }[];
}

export interface NutritionStageView {
  readonly target: NutritionTargetLine | null;
  readonly headline: string;
  readonly spanWords: string;
  readonly summary: string;
  readonly chart: NutritionChartView;
}

export interface NutritionStageInput {
  readonly target: NutritionTargetValues | null;
  readonly range: NutritionRange;
  readonly span: NutritionSpan;
  readonly nutrient: Nutrient;
  readonly weight: boolean;
}

export interface NutritionWords {
  readonly t: Translate;
  readonly locale: string;
}

/** Headroom over the tallest bar or target, so a value label above it stays inside the plot. */
const HEADROOM = 1.12;

/** The number a signed difference is printed with, „+280 kcal" or „−18 g białka". */
export function missText(miss: NutritionMiss, words: NutritionWords): string {
  return words.t(`${KEY}.miss.${miss.nutrient}`, { value: signedWhole(miss.difference, words.locale) });
}

/** A day's status in words, as the week table's status cell and the chart readout say it. */
export function dayStatusWords(day: NutritionDay, words: NutritionWords): string {
  const { t } = words;
  switch (toldStatus(day)) {
    case 'onTarget':
      return t(`${KEY}.status.onTarget`);
    case 'offTarget': {
      const first = day.reasons[0];
      return first === undefined
        ? t(`${KEY}.status.offTarget`)
        : t(`${KEY}.status.offTargetBy`, { miss: missText(first, words) });
    }
    case 'inProgress':
      return t(`${KEY}.status.inProgress`);
    case 'notLogged':
      return t(`${KEY}.status.notLogged`);
    case 'noTarget':
      return t(`${KEY}.status.noTarget`);
  }
}

export function buildNutritionStage(
  input: NutritionStageInput,
  words: NutritionWords,
): NutritionStageView {
  const { t } = words;
  const { range, target } = input;
  const summary = range.summary;
  const spanWords = t(`${KEY}.spanSentence.${input.span}`);
  const chart = buildChart(input, words);

  const headline =
    target === null || summary.judgedDays === 0
      ? t(`${KEY}.loggedDays`, { count: summary.loggedDays })
      : t(`${KEY}.onTargetDays`, { ok: summary.onTargetDays, count: summary.judgedDays });

  return {
    target: target === null ? null : targetLine(target, words),
    headline,
    spanWords,
    summary: summarySentence(input, chart.weight, words),
    chart,
  };
}

function targetLine(target: NutritionTargetValues, words: NutritionWords): NutritionTargetLine {
  const { locale } = words;
  const grams = (value: number | null) => (value === null ? null : withUnit(value, 'g', locale));
  return {
    since: dayMonth(target.effectiveFrom, locale),
    energy: withUnit(target.energy, 'kcal', locale),
    macros: [
      { nutrient: 'protein', value: grams(target.protein) },
      { nutrient: 'carbs', value: grams(target.carbs) },
      { nutrient: 'fat', value: grams(target.fat) },
    ],
  };
}

function summarySentence(
  input: NutritionStageInput,
  weight: NutritionWeightLine | null,
  words: NutritionWords,
): string {
  const { t, locale } = words;
  const { summary, days } = input.range;
  if (summary.loggedDays === 0) {
    return days.some(isLogged) ? t(`${KEY}.onlyTodayInRange`) : t(`${KEY}.noEntriesInRange`);
  }

  const avg = summary.averages;
  const n = (value: number) => wholeNumber(value, locale);
  const parts: string[] = [];
  if (avg.energy !== null) parts.push(t(`${KEY}.avg.energy`, { value: n(avg.energy) }));
  if (input.target === null) {
    return `${parts.join(', ')}. ${t(`${KEY}.unjudgedNote`)}`;
  }
  if (avg.protein !== null) parts.push(t(`${KEY}.avg.protein`, { value: n(avg.protein) }));
  if (avg.carbs !== null) {
    parts.push(
      avg.sugar === null
        ? t(`${KEY}.avg.carbs`, { value: n(avg.carbs) })
        : t(`${KEY}.avg.carbsSugar`, { value: n(avg.carbs), sugar: n(avg.sugar) }),
    );
  }
  if (avg.fat !== null) parts.push(t(`${KEY}.avg.fat`, { value: n(avg.fat) }));

  const sentences = [`${parts.join(', ')}.`];
  if (input.weight && weight !== null) {
    const first = weight.points[0];
    const last = weight.points[weight.points.length - 1];
    if (first !== undefined && last !== undefined) {
      const delta = Math.round((last.kg - first.kg) * 10) / 10;
      sentences.push(
        weight.points.length > 1
          ? t(`${KEY}.weightChange`, {
              kg: oneDecimal(last.kg, locale),
              delta: `${delta > 0 ? '+' : delta < 0 ? '−' : '±'}${oneDecimal(Math.abs(delta), locale)}`,
            })
          : t(`${KEY}.weightOnly`, { kg: oneDecimal(last.kg, locale) }),
      );
    }
  }
  const unlogged = days.filter((day) => !day.isToday && !isLogged(day)).length;
  if (unlogged > 0) sentences.push(t(`${KEY}.unloggedDays`, { count: unlogged }));
  return sentences.join(' ');
}

/* ── the chart ───────────────────────────────────────────────────────────────────────────────── */

function buildChart(input: NutritionStageInput, words: NutritionWords): NutritionChartView {
  const columns =
    input.span === 'quarter' ? weekColumns(input, words) : dayColumns(input, words);
  const unit = unitOf(input.nutrient);
  const tallest = Math.max(
    0,
    ...columns.map((column) => Math.max(column.value ?? 0, column.target ?? 0)),
  );
  const yMax = tallest === 0 ? 1 : tallest * HEADROOM;
  const step = yMax > 1500 ? 500 : yMax > 300 ? 100 : yMax > 60 ? 50 : 10;
  const yTicks: { value: number; label: string }[] = [];
  for (let value = 0; value <= yMax; value += step) {
    yTicks.push({ value, label: String(value) });
  }

  const steps = targetSteps(columns);
  const newest = columns[columns.length - 1];
  const lastTarget =
    newest === undefined || newest.target === null
      ? null
      : { value: newest.target, label: wholeNumber(newest.target, words.locale) };

  const weight = input.weight ? weightLine(columns, words) : null;
  const governed =
    input.span === 'quarter'
      ? (input.range.weeks ?? []).map((week) => week.target !== null)
      : input.range.days.map((day) => day.target !== null);
  const hasTarget = columns.some((column) => column.target !== null);

  return {
    columns,
    unit,
    yMax,
    yTicks,
    steps,
    lastTarget,
    weight,
    legend: legendFor(input, columns, hasTarget, governed, weight),
    name: words.t(hasTarget ? `${KEY}.chartName` : `${KEY}.chartNameUnjudged`, {
      nutrient: words.t(`${KEY}.nutrient.${input.nutrient}`),
      span: words.t(`${KEY}.spanSentence.${input.span}`),
    }),
  };
}

function dayColumns(input: NutritionStageInput, words: NutritionWords): NutritionChartColumn[] {
  const { t, locale } = words;
  const days = input.range.days;
  const nutrient = input.nutrient;
  const weekday = dateTimeFormat(locale, { weekday: 'short' });
  const longDay = dateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'short' });

  return days.map((day, index) => {
    const parsed = parseIsoDate(day.date);
    const value = day.totals[nutrient];
    const partial = isPartial(day, nutrient);
    const target = targetOf(day.target, nutrient);
    const fromEnd = days.length - 1 - index;
    const label = day.isToday
      ? t(`${KEY}.today`)
      : parsed === null
        ? null
        : input.span === 'week'
          ? `${weekday.format(parsed).replace(/\.$/, '')} ${String(parsed.getDate())}`
          : fromEnd % 7 === 0
            ? `${String(parsed.getDate())}.${String(parsed.getMonth() + 1)}`
            : null;
    const kind = day.verdicts[nutrient];
    return {
      key: day.date,
      label,
      current: day.isToday,
      kind,
      value: kind === 'none' ? null : value,
      sugar: nutrient === 'carbs' ? day.totals.sugar : null,
      target,
      valueLabel:
        input.span === 'week' && value !== null && kind !== 'none'
          ? atLeast(wholeNumber(value, locale), partial)
          : null,
      weightKg: day.weightKg,
      readout: {
        title: parsed === null ? day.date : capitaliseFirst(longDay.format(parsed), locale),
        lines: readoutLines(value, target, nutrient, dayReadoutStatus(day, nutrient, words), day.weightKg, words, {
          partial,
        }),
      },
    };
  });
}

function weekColumns(input: NutritionStageInput, words: NutritionWords): NutritionChartColumn[] {
  const { t, locale } = words;
  const nutrient = input.nutrient;
  return weekBundles(input.range).reverse().map(({ week, current }) => {
    const target = targetOf(week.target, nutrient);
    const value = week.averagesWithToday[nutrient];
    const verdict = week.verdicts[nutrient];
    const kind = current && verdict === 'none' && value !== null ? 'today' : verdict;
    const start = parseIsoDate(week.weekStart);
    const judged = week.summary.judgedDays;
    return {
      key: week.weekStart,
      label: start === null ? null : `${String(start.getDate())}.${String(start.getMonth() + 1)}`,
      current,
      kind,
      value: kind === 'none' ? null : value,
      sugar: nutrient === 'carbs' ? week.averagesWithToday.sugar : null,
      target,
      valueLabel: value === null || kind === 'none' ? null : wholeNumber(value, locale),
      weightKg: week.weightKg,
      readout: {
        title: weekSpanLabel(week.weekStart, week.weekEnd, locale),
        lines: readoutLines(
          value,
          target,
          nutrient,
          [
            judged === 0
              ? t(`${KEY}.weekUnjudged`)
              : t(`${KEY}.weekOnTarget`, { ok: week.summary.onTargetDays, count: judged }),
          ],
          week.weightKg,
          words,
          { average: true },
        ),
      },
    };
  });
}

/**
 * A judged day's readout on one nutrient's chart: that nutrient's verdict first, so the words agree
 * with the bar's ink, then the day's own verdict, named as the day's.
 */
function dayReadoutStatus(day: NutritionDay, nutrient: Nutrient, words: NutritionWords): string[] {
  const { t } = words;
  if (day.status !== 'onTarget' && day.status !== 'offTarget') return [dayStatusWords(day, words)];
  const verdict = day.verdicts[nutrient];
  const own = verdict === 'warn' ? day.reasons.find((miss) => miss.nutrient === nutrient) : undefined;
  const lines: string[] = [];
  if (verdict === 'ok') lines.push(t(`${KEY}.readoutVerdict.ok.${nutrient}`));
  if (verdict === 'warn') {
    lines.push(own === undefined ? t(`${KEY}.readoutVerdict.warn.${nutrient}`) : missText(own, words));
  }
  if (day.status === 'onTarget') return [...lines, t(`${KEY}.readoutDay.onTarget`)];
  const other = day.reasons.find((miss) => miss !== own);
  lines.push(
    other === undefined
      ? t(`${KEY}.readoutDay.offTarget`)
      : t(`${KEY}.readoutDay.offTargetBy`, { miss: missText(other, words) }),
  );
  return lines;
}

function readoutLines(
  value: number | null,
  target: number | null,
  nutrient: Nutrient,
  status: readonly string[],
  weightKg: number | null,
  words: NutritionWords,
  figure: { readonly average?: boolean; readonly partial?: boolean } = {},
): string[] {
  const { t, locale } = words;
  const unit = unitOf(nutrient);
  const lines: string[] = [];
  if (value !== null) {
    const amount = atLeast(withUnit(value, unit, locale), figure.partial ?? false);
    lines.push(figure.average ? t(`${KEY}.readoutAverage`, { value: amount }) : amount);
  }
  if (target !== null) {
    lines.push(t(`${KEY}.readoutTarget`, { value: withUnit(target, unit, locale) }));
  }
  lines.push(...status);
  if (weightKg !== null) lines.push(t(`${KEY}.readoutWeight`, { kg: oneDecimal(weightKg, locale) }));
  return lines;
}

/** One step per run of columns governed by the same target value (R3: a change is a step). */
export function targetSteps(
  columns: readonly { readonly target: number | null }[],
): NutritionTargetStep[] {
  const steps: NutritionTargetStep[] = [];
  let index = 0;
  while (index < columns.length) {
    const value = columns[index]?.target ?? null;
    let end = index;
    while (end + 1 < columns.length && (columns[end + 1]?.target ?? null) === value) end += 1;
    if (value !== null) steps.push({ from: index, to: end, value });
    index = end + 1;
  }
  return steps;
}

function weightLine(
  columns: readonly NutritionChartColumn[],
  words: NutritionWords,
): NutritionWeightLine | null {
  const points = columns.flatMap((column, index) =>
    column.weightKg === null ? [] : [{ index, kg: column.weightKg }],
  );
  const last = points[points.length - 1];
  if (last === undefined) return null;
  const all = points.map((point) => point.kg);
  const lo = Math.floor(Math.min(...all) - 0.5);
  const hi = Math.ceil(Math.max(...all) + 0.5);
  const kg = (value: number) => `${oneDecimal(value, words.locale)}\u00a0kg`;
  return {
    points,
    lo,
    hi,
    ticks: [lo, hi].map((value) => ({ kg: value, label: kg(value) })),
    lastLabel: kg(last.kg),
  };
}

function legendFor(
  input: NutritionStageInput,
  columns: readonly NutritionChartColumn[],
  hasTarget: boolean,
  governed: readonly boolean[],
  weight: NutritionWeightLine | null,
): NutritionLegendItem[] {
  const items: NutritionLegendItem[] = [];
  const weekly = input.span === 'quarter';
  if (!hasTarget) items.push('unjudged');
  else {
    if (columns.some((column) => column.kind === 'ok' || column.kind === 'warn')) {
      items.push('onTarget', 'offTarget');
    }
    items.push(targetRuleOf(input.nutrient).kind === 'floor' ? 'targetFloor' : 'targetBand');
    // „bez celu dla tego makro" only where a target governed but left this macro out.
    const plain = columns.flatMap((column, index) => (column.kind === 'plain' ? [governed[index] ?? false] : []));
    if (plain.some((isGoverned) => !isGoverned || input.nutrient === 'energy')) items.push('noTarget');
    else if (plain.length > 0) items.push('plain');
  }
  items.push('current');
  if (input.nutrient === 'carbs') items.push('sugar');
  if (weekly) items.push('average');
  if (weight !== null) items.push(weekly ? 'weightWeekly' : 'weight');
  return items;
}

/** A legend item's words; the target line's carries its nutrient's margin from `targetRuleOf`. */
export function legendLabel(item: NutritionLegendItem, nutrient: Nutrient, t: Translate): string {
  return item === 'targetBand' || item === 'targetFloor'
    ? t(`${KEY}.legend.${item}`, { percent: targetRuleOf(nutrient).percent })
    : t(`${KEY}.legend.${item}`);
}

/**
 * „dziś 16:45", „wczoraj 9:10", „12 paź 18:00" — when the client last wrote anything, on the
 * TRAINER's clock and calendar, since it is the trainer who reads "today".
 */
export function lastEntryWords(at: string, now: Date, words: NutritionWords): string | null {
  const instant = new Date(at);
  if (Number.isNaN(instant.getTime())) return null;
  const { t, locale } = words;
  const time = dateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(instant);
  const dayOf = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const days = Math.round((dayOf(now) - dayOf(instant)) / 86_400_000);
  if (days === 0) return t(`${KEY}.lastEntryToday`, { time });
  if (days === 1) return t(`${KEY}.lastEntryYesterday`, { time });
  return t(`${KEY}.lastEntryOn`, {
    date: dateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(instant),
    time,
  });
}
